import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';
import { AnalyticsService } from '../../services/analytics.service';

/**
 * 🔐 RESET PASSWORD COMPONENT
 *
 * FLOW:
 * 1. Récupère token et email depuis les query params
 * 2. Valide le token auprès du backend
 * 3. Affiche formulaire de réinitialisation (si token valide)
 * 4. Utilisateur entre son nouveau mot de passe
 * 5. Envoie requête POST /api/auth/reset-password
 * 6. Redirige vers login après succès
 *
 * SÉCURITÉ:
 * - Validation du token avant affichage du formulaire
 * - Token expiré = message d'erreur avec lien pour demander un nouveau
 * - Mot de passe force minuscule + majuscule + chiffre + caractère spécial
 * - Token utilisé une seule fois (supprimé après succès)
 */
@Component({
  selector: 'app-reset-password',
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css'],
})
export class ResetPasswordComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  resetPasswordForm: FormGroup;
  isLoading = false;
  isValidating = true; // Spinner pendant la validation du token
  isTokenValid = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  token: string | null = null;
  email: string | null = null;

  // Gestion des erreurs de validation de formulaire
  showPasswordError = false;
  showPasswordStrengthError = false;

  constructor() {
    this.resetPasswordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  ngOnInit(): void {
    this.log.debug('🔐 ResetPasswordComponent initialized');

    // 📍 Récupère les paramètres de l'URL
    this.route.queryParams.subscribe((params) => {
      this.token = params['token'] || null;
      this.email = params['email'] || null;

      this.log.debug(
        `🔐 Query params - token: ${this.token ? 'present' : 'missing'}, email: ${this.email}`,
      );

      if (!this.token || !this.email) {
        this.log.debug('❌ Missing token or email in URL');
        this.errorMessage =
          'Lien de réinitialisation invalide. Veuillez demander un nouveau lien.';
        this.isValidating = false;
        this.cdr.markForCheck();
        return;
      }

      // 🔐 Valide le token auprès du backend
      this.validateToken();
      this.cdr.markForCheck();
    });
  }

  /**
   * 🔐 Valide le token avec le backend
   *
   * FLOW:
   * 1. Appelle authService.validateResetToken(token, email)
   * 2. Si valide: affiche formulaire
   * 3. Si invalide/expiré: affiche erreur
   */
  private validateToken(): void {
    this.log.debug(`🔐 Validating token for email: ${this.email}`);

    if (!this.token || !this.email) {
      this.isValidating = false;
      return;
    }

    this.authService.validateResetToken(this.token, this.email).subscribe({
      next: (response) => {
        this.log.debug('✅ Token is valid');
        this.isTokenValid = true;
        this.isValidating = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('❌ Token validation failed:', err);
        this.isTokenValid = false;
        this.errorMessage =
          'Ce lien de réinitialisation a expiré ou est invalide. Veuillez demander un nouveau lien.';
        this.isValidating = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * 🔒 Validateur personnalisé: les mots de passe doivent correspondre
   */
  private passwordMatchValidator(
    group: FormGroup,
  ): { [key: string]: any } | null {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  /**
   * 📧 Soumet la réinitialisation du mot de passe
   *
   * FLOW:
   * 1. Valide le formulaire
   * 2. Valide la force du mot de passe
   * 3. Appelle authService.resetPassword(token, email, password)
   * 4. Redirige vers login après succès
   */
  onSubmit(): void {
    this.log.debug('🔐 Submitting reset password form');

    if (this.resetPasswordForm.invalid) {
      this.log.debug('⚠️ Form invalid, marking as touched');
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    // 🔐 Valide la force du mot de passe
    const password = this.resetPasswordForm.get('password')?.value;
    if (!this.isPasswordStrong(password)) {
      this.log.debug('⚠️ Password not strong enough');
      this.showPasswordStrengthError = true;
      return;
    }

    if (!this.token || !this.email) {
      this.log.debug('❌ Missing token or email');
      this.errorMessage = 'Lien invalide. Veuillez réessayer.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;

    this.log.debug(`🔐 Calling resetPassword for email: ${this.email}`);

    this.authService.resetPassword(this.token, this.email, password).subscribe({
      next: (response) => {
        this.log.debug('✅ Password reset succeeded');
        this.analytics.trackMotDePasseReset();
        this.successMessage =
          'Votre mot de passe a été réinitialisé avec succès ! Vous allez être redirigé vers la connexion...';

        // Redirige après 2 secondes
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('❌ Password reset error:', err);
        this.errorMessage =
          err.error?.message || 'Une erreur est survenue. Veuillez réessayer.';
        this.cdr.markForCheck();
      },
      complete: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * 🔐 Valide la force du mot de passe
   * Exigences:
   * - Minimum 8 caractères
   * - Au moins une majuscule
   * - Au moins une minuscule
   * - Au moins un chiffre
   * - Au moins un caractère spécial (@$!%*?&)
   */
  public isPasswordStrong(password: string): boolean {
    const strongRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return strongRegex.test(password);
  }

  /**
   * 📝 Retourne le message de force du mot de passe
   */
  getPasswordStrengthMessage(): string {
    return 'Le mot de passe doit contenir au minimum 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.';
  }

  /**
   * 🔄 Retour à la page de demande de réinitialisation
   */
  goToForgotPassword(): void {
    this.log.debug('🔄 Navigating to forgot-password');
    this.router.navigate(['/forgot-password']);
  }
}
